import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/system';
import axios from 'axios';
import { motion, useInView } from 'framer-motion';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';

// Styled Box for USD vs BTC Value Display with Animated Border
const AnimatedGlowBox = styled(motion(Box), {
  shouldForwardProp: (prop) => prop !== 'godMode',
})(({ theme, godMode }) => ({
  width: '480px',
  // Auto height with a minimum: a fixed height clipped the top/bottom of
  // the text on tablet-sized screens (overflow is hidden for the glow box).
  height: 'auto',
  minHeight: '600px',
  padding: '40px',          // Reduced padding slightly for more text space
  margin: '20px auto',
  // In God Mode the box goes transparent and the god circle renders on a
  // separate stationary layer behind it (see render), so dragging the box
  // aside leaves the image in place for the viewer to see.
  backgroundColor: godMode ? 'transparent' : '#1a237e',
  borderRadius: '10px',
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  boxSizing: 'border-box',
  
  [theme.breakpoints.down('md')]: {
    width: '400px',
    height: 'auto',         // Auto prevents top/bottom clipping on tablets
    minHeight: '500px',
    padding: '30px',
  },

  [theme.breakpoints.down('sm')]: {
    width: '90vw',
    height: 'auto',         // Changed to auto for mobile to prevent cutting
    minHeight: '400px',     // Minimum height for mobile
    maxWidth: '350px',
    padding: '20px',
  },
}));

// Retro styled Typography for Title
const TitleText = styled(Typography)(({ theme }) => ({
  fontFamily: "'Press Start 2P', cursive",
  color: '#FFFFFF',
  fontSize: '1.6rem',       // Reduced from 2rem
  marginBottom: '20px',     // Increased margin for better spacing
  lineHeight: '1.4',        // Added line height for better readability
  textShadow: `
    -1px -1px 0 #000,
    1px -1px 0 #000,
    -1px 1px 0 #000,
    1px 1px 0 #000
  `,
  '@media (max-width: 600px)': {
    fontSize: '0.9rem',     // Adjusted for mobile
    marginBottom: '15px',
  },
}));

// Retro styled Typography for Retro B
const RetroB = styled(Typography)(({ theme }) => ({
  fontFamily: "'Press Start 2P', cursive",
  color: '#FFFFFF',
  fontSize: '1.4rem',       // Reduced from 1.8rem
  marginLeft: '5px',
  textShadow: `
    -1px -1px 0 #000,
    1px -1px 0 #000,
    -1px 1px 0 #000,
    1px 1px 0 #000
  `,
  '@media (max-width: 600px)': {
    fontSize: '0.7rem',
  },
}));

// Satoshi Value Styling
const SatoshiValueText = styled(Typography)(({ theme }) => ({
  fontFamily: "'Press Start 2P', cursive",
  color: '#FFFFFF',
  fontSize: '1.1rem',       // Reduced from 1.4rem
  marginTop: '20px',
  lineHeight: '1.4',
  textShadow: `
    -1px -1px 0 #000,
    1px -1px 0 #000,
    -1px 1px 0 #000,
    1px 1px 0 #000
  `,
  '@media (max-width: 600px)': {
    fontSize: '0.7rem',
    marginTop: '15px',
  },
}));

// Satoshi Label Styling
const SatoshiLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "'Press Start 2P', cursive",
  color: '#FFFFFF',
  fontSize: '1.2rem',       // Reduced from 1.8rem
  marginTop: '15px',        // Increased margin
  lineHeight: '1.4',        // Added line height
  textShadow: `
    -1px -1px 0 #000,
    1px -1px 0 #000,
    -1px 1px 0 #000,
    1px 1px 0 #000
  `,
  '@media (max-width: 600px)': {
    fontSize: '0.8rem',
    marginTop: '10px',
  },
}));

const UsdVsBtcComparison = ({ godMode = false }) => {
  const [btcValue, setBtcValue] = useState(null);
  const [satoshiValue, setSatoshiValue] = useState(null);
  const [sportsCarSats, setSportsCarSats] = useState(null);
  const boxRef = useRef(null);
  const isInView = useInView(boxRef, { once: true, margin: '-100px' });

  useEffect(() => {
    const fetchBTCValue = async () => {
      try {
        const response = await axios.get('/api/btc-price'); // Use the existing endpoint
        const btcPrice = response.data.price;
        const usdAmount = 100;
        const btcEquivalent = (usdAmount / btcPrice).toFixed(6);
        setBtcValue(btcEquivalent);

        // Calculate and format satoshi value with commas
        const satoshiEquivalent = Math.round(btcEquivalent * 100000000);
        const formattedSatoshiValue = satoshiEquivalent.toLocaleString();
        setSatoshiValue(formattedSatoshiValue);

        // Calculate sports car ($50,000) in satoshis
        const sportsCarAmount = 50000;
        const sportsCarBtc = sportsCarAmount / btcPrice;
        const sportsCarSatoshis = Math.round(sportsCarBtc * 100000000);
        const formattedSportsCarSats = sportsCarSatoshis.toLocaleString();
        setSportsCarSats(formattedSportsCarSats);
      } catch (error) {
        console.error('Error fetching BTC value:', error);
        setBtcValue('N/A');
        setSatoshiValue('N/A');
        setSportsCarSats('N/A');
      }
    };

    fetchBTCValue();
  }, []);

  return (
    <Box
      sx={{
        position: 'relative',
        margin: '20px auto',
        width: { xs: '90vw', sm: '400px', md: '480px' },
        maxWidth: { xs: '350px', sm: 'none' },
      }}
    >
      {/* Stationary god circle layer: stays put while the box is dragged
          aside, so the viewer can see the image unobstructed. */}
      {godMode && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(https://media.satoshiadvance.com/images/GodCircleSeven.png)',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center center',
            backgroundSize: 'contain',
            borderRadius: '10px',
            pointerEvents: 'none',
          }}
        />
      )}
    <AnimatedGlowBox
      godMode={godMode}
      ref={boxRef}
      style={{ margin: 0, width: '100%' }}
      // Click-and-drag left/right (same feel as the spinner logo) so the box
      // can be slid out of the way of the god circle imagery.
      drag="x"
      dragMomentum={false}
      dragElastic={0.15}
      dragConstraints={{ left: -600, right: 600 }}
      whileDrag={{ cursor: 'grabbing' }}
      sx={{ cursor: 'grab' }}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1, boxShadow: '0 0 20px #9932CC, 0 0 30px #9932CC, 0 0 40px #9932CC' } : {}}
      whileHover={{
        boxShadow: '0 0 40px #BA55D3, 0 0 60px #BA55D3, 0 0 80px #BA55D3', // Brighter glow when hovered
      }}
      whileTap={{
        boxShadow: '0 0 50px #9400D3, 0 0 70px #9400D3, 0 0 90px #9400D3', // Even more intense glow when clicked
      }}
      transition={{
        duration: 1.5,
        ease: 'easeInOut',
      }}
    >
      <TitleText>
        Current value of $100 USD in BTC
      </TitleText>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '10px' }}>
        <Typography
          sx={{
            fontFamily: "'Press Start 2P', cursive",
            color: '#FFFFFF',
            marginRight: '5px',
            fontSize: '1.4rem',      // Reduced from 1.8rem
            textShadow: `
              -1px -1px 0 #000,
              1px -1px 0 #000,
              -1px 1px 0 #000,
              1px 1px 0 #000
            `,
            '@media (max-width: 600px)': {
              fontSize: '0.7rem',
            },
          }}
        >
          $100.0 USD is
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Press Start 2P', cursive",
            color: '#FFFFFF',
            fontSize: '1.4rem',      // Reduced from 1.8rem
            textShadow: `
              -1px -1px 0 #000,
              1px -1px 0 #000,
              -1px 1px 0 #000,
              1px 1px 0 #000
            `,
            '@media (max-width: 600px)': {
              fontSize: '0.7rem',
            },
          }}
        >
          {btcValue || 'Loading...'}
        </Typography>
        <RetroB>
          B
        </RetroB>
      </Box>
      {satoshiValue && (
        <>
          <SatoshiValueText>
            {satoshiValue} Satoshi's or
          </SatoshiValueText>
          <SatoshiLabel>
            "Sats" Value since there are 100 million satoshi's in 1 BTC
          </SatoshiLabel>
        </>
      )}
      {sportsCarSats && (
        <Box sx={{ marginTop: '20px', textAlign: 'center' }}>
          <Typography
            sx={{
              fontFamily: "'Press Start 2P', cursive",
              color: '#FFFFFF',
              fontSize: '0.8rem',
              marginBottom: '10px',
              textShadow: `
                -1px -1px 0 #000,
                1px -1px 0 #000,
                -1px 1px 0 #000,
                1px 1px 0 #000
              `,
              '@media (max-width: 600px)': {
                fontSize: '0.6rem',
              },
            }}
          >
            How many Sats are in $50,000?
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <Typography
              sx={{
                fontFamily: "'Press Start 2P', cursive",
                color: '#00FF00',
                fontSize: '1.1rem',
                textShadow: `
                  -1px -1px 0 #000,
                  1px -1px 0 #000,
                  -1px 1px 0 #000,
                  1px 1px 0 #000
                `,
                '@media (max-width: 600px)': {
                  fontSize: '0.8rem',
                },
              }}
            >
              {sportsCarSats} Sats
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <Typography
                sx={{
                  fontFamily: "'Press Start 2P', cursive",
                  color: '#FFFFFF',
                  fontSize: '0.6rem',
                  textShadow: `
                    -1px -1px 0 #000,
                    1px -1px 0 #000,
                    -1px 1px 0 #000,
                    1px 1px 0 #000
                  `,
                  '@media (max-width: 600px)': {
                    fontSize: '0.5rem',
                  },
                }}
              >
                Nice Ride
              </Typography>
              <DirectionsCarIcon
                sx={{
                  color: '#FFFFFF',
                  fontSize: '1.5rem',
                  filter: 'drop-shadow(-1px -1px 0px #000) drop-shadow(1px -1px 0px #000) drop-shadow(-1px 1px 0px #000) drop-shadow(1px 1px 0px #000)',
                  '@media (max-width: 600px)': {
                    fontSize: '1.2rem',
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      )}
    </AnimatedGlowBox>
    </Box>
  );
};

export default UsdVsBtcComparison;

